'use client';

/**
 * GradientText Component
 *
 * Typography with gradient coloring and optional animation.
 */

import { keyframes, styled } from '@mui/material/styles';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { baseColors, gradients } from '../../theme/palette';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface GradientTextProps extends Omit<TypographyProps, 'color'> {
  /**
   * Gradient preset or custom gradient
   * @default 'chroma'
   */
  gradient?: 'chroma' | 'primary' | 'secondary' | 'success' | 'neon' | 'holographic' | 'aurora' | 'custom';

  /**
   * Custom gradient (when gradient='custom')
   */
  customGradient?: string;

  /**
   * Enable animated gradient shift
   * @default false
   */
  animated?: boolean;

  /**
   * Animation duration in seconds
   * @default 3
   */
  animationDuration?: number;

  /**
   * Text glow effect
   * @default false
   */
  glow?: boolean;

  /**
   * Pulsing text glow animation (breathing text-shadow).
   * @default false
   */
  pulseGlow?: boolean;

  /**
   * Children text content
   */
  children?: React.ReactNode;
}

// ============================================================================
// Animations
// ============================================================================

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// ============================================================================
// Gradient Presets
// ============================================================================

const gradientPresets: Record<string, string> = {
  chroma: gradients.chroma,
  primary: `linear-gradient(135deg, ${baseColors.primary[400]} 0%, ${baseColors.primary[600]} 100%)`,
  secondary: `linear-gradient(135deg, ${baseColors.secondary[400]} 0%, ${baseColors.secondary[600]} 100%)`,
  success: `linear-gradient(135deg, ${baseColors.success[400]} 0%, ${baseColors.success[600]} 100%)`,
  neon: gradients.neonPulse,
  holographic: gradients.holographic,
  aurora: gradients.aurora,
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextProps {
  $gradient: string;
  $animated: boolean;
  $animationDuration: number;
  $glow: boolean;
  $pulseGlow: boolean;
}

const textGlowPulse = keyframes`
  0%, 100% { text-shadow: 0 0 20px rgba(99, 91, 255, 0.2), 0 0 40px rgba(0, 212, 255, 0.1); }
  50% { text-shadow: 0 0 30px rgba(99, 91, 255, 0.4), 0 0 60px rgba(0, 212, 255, 0.25); }
`;

const StyledText = styled(
  Typography,
  transientOptions,
)<StyledTextProps>(({ $gradient, $animated, $animationDuration, $glow, $pulseGlow }) => ({
  background: $gradient,
  backgroundSize: $animated ? '200% 200%' : '100% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',

  ...($animated && {
    animation: `${gradientShift} ${$animationDuration}s ease infinite`,
  }),

  ...($glow && {
    textShadow: '0 0 30px rgba(99, 91, 255, 0.3), 0 0 60px rgba(0, 212, 255, 0.2)',
  }),

  ...($pulseGlow && {
    animation: $animated
      ? `${gradientShift} ${$animationDuration}s ease infinite, ${textGlowPulse} 3s ease-in-out infinite`
      : `${textGlowPulse} 3s ease-in-out infinite`,
  }),
}));

// ============================================================================
// Component
// ============================================================================

export const GradientText = forwardRef<HTMLSpanElement, GradientTextProps>(
  (
    {
      children,
      gradient = 'chroma',
      customGradient,
      animated = false,
      animationDuration = 3,
      glow = false,
      pulseGlow = false,
      variant = 'h4',
      ...props
    },
    ref,
  ) => {
    const gradientValue =
      gradient === 'custom' && customGradient ? customGradient : gradientPresets[gradient] || gradients.chroma;

    const textContent = (
      <StyledText
        ref={ref}
        variant={variant}
        $gradient={gradientValue}
        $animated={animated}
        $animationDuration={animationDuration}
        $glow={glow}
        $pulseGlow={pulseGlow}
        {...props}
      >
        {children}
      </StyledText>
    );

    // Wrap in motion div for entrance animation if desired
    if (animated) {
      return (
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.215, 0.61, 0.355, 1] }}
          style={{ display: 'inline-block' }}
        >
          {textContent}
        </motion.span>
      );
    }

    return textContent;
  },
);

GradientText.displayName = 'GradientText';

export default GradientText;
